/**
 * 開いているノート1件のメタ情報（activeFile）。
 *
 * これまでは `state.files`（File Picker 用の全ノート一覧）から毎回 find していたが、
 * ノートが数千件に増えると起動時に全一覧を受け取ること自体が重い。編集画面が必要とするのは
 * 開いている1件だけなので、その1件を `state.activeFile` に分けて持つ。
 *
 * サーバーは `api/tree.php` などのレスポンスに `active_file` を付ける。まだ付けない経路や
 * 古いレスポンスのために、無ければ一覧から拾う経路も残す。
 */

export interface ActiveFileHolder {
  files: any[];
  activeFileId: number | null;
  activeFile?: any;
}

function findInFileList(state: ActiveFileHolder, fileId: number | null): any {
  if (fileId === null || fileId === undefined) {
    return null;
  }
  return state.files.find((file) => Number(file.id) === Number(fileId)) || null;
}

/** 開いているノート。無ければ一覧から拾う。 */
export function getActiveFile(state: ActiveFileHolder): any {
  const active = state.activeFile;
  if (active && Number(active.id) === Number(state.activeFileId)) {
    return active;
  }
  return findInFileList(state, state.activeFileId);
}

/**
 * レスポンスから activeFile を決める。`active_file` があればそれを正とする。
 *
 * 本文同期のようにノートのメタ情報を返さない応答では、同じノートを開いている間は
 * 既存の activeFile を保持する。起動時に全ノート一覧を省いている場合、ここで消すと
 * テーマや閲覧専用設定を一覧から復元できない。
 */
export function setActiveFileFromResponse(state: ActiveFileHolder, data: any): void {
  const fromResponse = data && data.active_file ? data.active_file : null;
  if (fromResponse) {
    state.activeFile = fromResponse;
    return;
  }

  const fromList = findInFileList(state, state.activeFileId);
  if (fromList) {
    state.activeFile = fromList;
    return;
  }

  if (state.activeFile && Number(state.activeFile.id) === Number(state.activeFileId)) {
    return;
  }
  state.activeFile = null;
}

/**
 * 更新APIが返した1件を一覧へ反映する。
 *
 * 更新APIは全ノート一覧を返さなくなったため、作成・改名・設定変更のたびに
 * 全件を取り直すのではなく、変わった1件だけを手元の一覧へ入れる。
 * 一覧をまだ持っていない（起動時に省いた）場合は、何もしない。
 */
export function upsertFile(state: ActiveFileHolder, file: any): void {
  if (!file || !(Number(file.id) > 0)) {
    return;
  }
  const index = state.files.findIndex((item) => Number(item.id) === Number(file.id));
  if (index >= 0) {
    // 返ってきた項目だけを上書きする。軽量なレスポンスで既存の項目を消さない。
    state.files[index] = { ...state.files[index], ...file };
  } else if (state.files.length > 0) {
    state.files.push({ ...file });
  }
  if (state.activeFile && Number(state.activeFile.id) === Number(file.id)) {
    state.activeFile = { ...state.activeFile, ...file };
  }
}

/** 削除したノートを一覧から外す。 */
export function removeFile(state: ActiveFileHolder, fileId: number): void {
  state.files = state.files.filter((file) => Number(file.id) !== Number(fileId));
}

/** テーマや表示モードのように、1項目だけ変わったときの局所更新。 */
export function patchActiveFile(state: ActiveFileHolder, fileId: number | null, patch: object): void {
  if (fileId === null || fileId === undefined) {
    return;
  }
  const inList = findInFileList(state, fileId);
  if (inList) {
    Object.assign(inList, patch);
  }
  if (state.activeFile && Number(state.activeFile.id) === Number(fileId)) {
    Object.assign(state.activeFile, patch);
  }
}
